/**
 * Subscription Types for TradeTitans
 * Four-Tier System with Security Mechanisms and Work Order Limits
 */

export enum SubscriptionTier {
  SOLO = 'solo',
  BASIC_PLUS = 'basic_plus',
  PROFESSIONAL = 'professional',
  PREMIUM = 'premium',
  SAAS_ENGINEER = 'saas_engineer',
  PERPETUAL_LICENSE = 'perpetual_license'
}

export enum BillingCycle {
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  YEARLY = 'yearly'
}

export enum SubscriptionStatus {
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  SUSPENDED = 'suspended',
  EXPIRED = 'expired',
  TRIAL = 'trial'
}

export interface SubscriptionPlan {
  id: string;
  tenant_id: string;
  tier: SubscriptionTier;
  billing_cycle: BillingCycle;
  status: SubscriptionStatus;
  current_price: number;
  start_date: string;
  end_date?: string;
  trial_end_date?: string;
  features: string[];
  limits: SubscriptionLimits;
  usage: SubscriptionUsage;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionLimits {
  maxEmployees: number;
  maxProperties: number;
  maxAssets: number;
  maxWorkOrdersPerMonth: number;
  workOrderOverageRate: number; // $1.99 per additional work order
  storageGB: number;
  apiCallsPerDay: number;
  maxDevices: number; // Security: limit concurrent devices
  maxSessions: number; // Security: limit concurrent sessions
}

export interface SubscriptionUsage {
  employees: number;
  properties: number;
  assets: number;
  workOrdersThisMonth: number;
  workOrderOverageCharges: number;
  storageUsedGB: number;
  apiCallsToday: number;
  activeDevices: number;
  activeSessions: number;
}

export interface DeviceSession {
  id: string;
  user_id: string;
  tenant_id: string;
  device_fingerprint: string;
  device_info: DeviceInfo;
  ip_address: string;
  user_agent: string;
  location_data?: LocationData;
  session_token: string;
  is_active: boolean;
  last_activity: string;
  created_at: string;
  expires_at: string;
}

export interface DeviceInfo {
  userAgent: string;
  platform: string;
  language: string;
  screenSize: string;
  timezone: string;
  hardwareConcurrency?: number;
  deviceMemory?: number;
  fingerprint: string;
}

export interface LocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: number;
}

export interface SecuritySettings {
  maxConcurrentDevices: number;
  maxConcurrentSessions: number;
  sessionTimeoutMinutes: number;
  deviceVerificationRequired: boolean;
  locationTrackingEnabled: boolean;
  forceLogoutOnNewDevice: boolean;
  suspiciousActivityDetection: boolean;
}

// Subscription tier configurations
export const SUBSCRIPTION_TIERS: Record<SubscriptionTier, {
  name: string;
  price: Record<BillingCycle, number>;
  features: string[];
  limits: SubscriptionLimits;
  security: SecuritySettings;
}> = {
  [SubscriptionTier.SOLO]: {
    name: 'Solo Plan',
    price: {
      [BillingCycle.MONTHLY]: 49,
      [BillingCycle.QUARTERLY]: 130,
      [BillingCycle.YEARLY]: 490
    },
    features: [
      'Work Order Management (20/month)',
      'Asset Tracking (50 assets)',
      'Basic Scheduling',
      'Mobile App Access',
      'Email Support',
      'Basic Reports',
      'QR Code Generation',
      'Photo Upload (2GB)',
      'Single Device Security',
      '$1.99 per additional work order'
    ],
    limits: {
      maxEmployees: 1,
      maxProperties: 5,
      maxAssets: 50,
      maxWorkOrdersPerMonth: 20,
      workOrderOverageRate: 1.99,
      storageGB: 2,
      apiCallsPerDay: 500,
      maxDevices: 1, // Single device only
      maxSessions: 1
    },
    security: {
      maxConcurrentDevices: 1,
      maxConcurrentSessions: 1,
      sessionTimeoutMinutes: 30,
      deviceVerificationRequired: true,
      locationTrackingEnabled: false,
      forceLogoutOnNewDevice: true,
      suspiciousActivityDetection: false
    }
  },
  [SubscriptionTier.BASIC_PLUS]: {
    name: 'Basic Plan Plus',
    price: {
      [BillingCycle.MONTHLY]: 99,
      [BillingCycle.QUARTERLY]: 270,
      [BillingCycle.YEARLY]: 990
    },
    features: [
      'Work Order Management (75/month)',
      'Asset Tracking (200 assets)',
      'Advanced Scheduling',
      'Mobile App Access',
      'Priority Email Support',
      'Enhanced Reports',
      'QR Code Generation',
      'Photo Upload (10GB)',
      'Multi-Device Security (2 devices)',
      'Basic Training Resources',
      '$1.99 per additional work order'
    ],
    limits: {
      maxEmployees: 10,
      maxProperties: 25,
      maxAssets: 200,
      maxWorkOrdersPerMonth: 75,
      workOrderOverageRate: 1.99,
      storageGB: 10,
      apiCallsPerDay: 2000,
      maxDevices: 2,
      maxSessions: 2
    },
    security: {
      maxConcurrentDevices: 2,
      maxConcurrentSessions: 2,
      sessionTimeoutMinutes: 45,
      deviceVerificationRequired: true,
      locationTrackingEnabled: true,
      forceLogoutOnNewDevice: false,
      suspiciousActivityDetection: true
    }
  },
  [SubscriptionTier.PROFESSIONAL]: {
    name: 'Professional Plan',
    price: {
      [BillingCycle.MONTHLY]: 199,
      [BillingCycle.QUARTERLY]: 540,
      [BillingCycle.YEARLY]: 1990
    },
    features: [
      'Work Order Management (150/month)',
      'Asset Tracking (500 assets)',
      'Advanced Scheduling',
      'Mobile App Access',
      'Priority Phone Support',
      'Advanced Analytics',
      'QR Code Generation',
      'Photo Upload (25GB)',
      'Multi-Device Security (5 devices)',
      'Scheduled Maintenance Visits',
      'Specialized Training Resources',
      'API Access',
      '$1.99 per additional work order'
    ],
    limits: {
      maxEmployees: 50,
      maxProperties: 100,
      maxAssets: 500,
      maxWorkOrdersPerMonth: 150,
      workOrderOverageRate: 1.99,
      storageGB: 25,
      apiCallsPerDay: 5000,
      maxDevices: 5,
      maxSessions: 5
    },
    security: {
      maxConcurrentDevices: 5,
      maxConcurrentSessions: 5,
      sessionTimeoutMinutes: 60,
      deviceVerificationRequired: true,
      locationTrackingEnabled: true,
      forceLogoutOnNewDevice: false,
      suspiciousActivityDetection: true
    }
  },
  [SubscriptionTier.PREMIUM]: {
    name: 'Premium Plan',
    price: {
      [BillingCycle.MONTHLY]: 499,
      [BillingCycle.QUARTERLY]: 1350,
      [BillingCycle.YEARLY]: 4990
    },
    features: [
      'Unlimited Work Orders',
      'Unlimited Asset Tracking',
      'Advanced Scheduling',
      'Mobile App Access',
      '24/7 Emergency Support',
      'Advanced Analytics',
      'QR Code Generation',
      'Photo Upload (100GB)',
      'Multi-Device Security (unlimited)',
      'Customized Maintenance Plans',
      'Advanced Training Sessions',
      'Premium Tools & Resources',
      'API Access',
      'Custom Branding',
      'Integration Support',
      'Dedicated Account Manager'
    ],
    limits: {
      maxEmployees: -1, // Unlimited
      maxProperties: -1, // Unlimited
      maxAssets: -1, // Unlimited
      maxWorkOrdersPerMonth: -1, // Unlimited
      workOrderOverageRate: 0, // No overage charges
      storageGB: 100,
      apiCallsPerDay: 50000,
      maxDevices: -1, // Unlimited
      maxSessions: -1
    },
    security: {
      maxConcurrentDevices: -1, // Unlimited
      maxConcurrentSessions: -1,
      sessionTimeoutMinutes: 120,
      deviceVerificationRequired: true,
      locationTrackingEnabled: true,
      forceLogoutOnNewDevice: false,
      suspiciousActivityDetection: true
    }
  },
  [SubscriptionTier.SAAS_ENGINEER]: {
    name: 'SaaS Engineer Plan',
    price: {
      [BillingCycle.MONTHLY]: 1500, // Negotiated: $1,500-$5,000
      [BillingCycle.QUARTERLY]: 4000,
      [BillingCycle.YEARLY]: 15000
    },
    features: [
      'Unlimited Work Orders',
      'Unlimited Asset Tracking',
      'Advanced Scheduling with PM System',
      'Mobile App Access',
      '24/7 Emergency Support',
      'Advanced Analytics & Reporting',
      'QR Code Generation & Scanning',
      'Photo Upload (500GB)',
      'Multi-Device Security (unlimited)',
      'Parts Inventory Management',
      'Preventive Maintenance System',
      'Scheduled PM Work Orders',
      'Advanced Training Sessions',
      'Premium Tools & Resources',
      'API Access',
      'Custom Branding',
      'Integration Support',
      'Dedicated Account Manager',
      'Hotel/Apartment Specific Features',
      'Multi-Unit Property Management',
      'Enhanced Financial Management',
      'Advanced Tracking & GPS',
      'Custom Workflow Automation'
    ],
    limits: {
      maxEmployees: -1, // Unlimited
      maxProperties: -1, // Unlimited
      maxAssets: -1, // Unlimited
      maxWorkOrdersPerMonth: -1, // Unlimited
      workOrderOverageRate: 0, // No overage charges
      storageGB: 500,
      apiCallsPerDay: 100000,
      maxDevices: -1, // Unlimited
      maxSessions: -1
    },
    security: {
      maxConcurrentDevices: -1, // Unlimited
      maxConcurrentSessions: -1,
      sessionTimeoutMinutes: 180,
      deviceVerificationRequired: true,
      locationTrackingEnabled: true,
      forceLogoutOnNewDevice: false,
      suspiciousActivityDetection: true
    }
  },
  [SubscriptionTier.PERPETUAL_LICENSE]: {
    name: 'Perpetual License',
    price: {
      [BillingCycle.MONTHLY]: 0, // One-time fee
      [BillingCycle.QUARTERLY]: 0,
      [BillingCycle.YEARLY]: 0
    },
    features: [
      'Unlimited Work Orders (Lifetime)',
      'Unlimited Asset Tracking (Lifetime)',
      'Advanced Scheduling with PM System',
      'Mobile App Access (Lifetime)',
      '24/7 Emergency Support (Lifetime)',
      'Advanced Analytics & Reporting',
      'QR Code Generation & Scanning',
      'Photo Upload (1TB)',
      'Multi-Device Security (unlimited)',
      'Parts Inventory Management',
      'Preventive Maintenance System',
      'Scheduled PM Work Orders',
      'Advanced Training Sessions',
      'Premium Tools & Resources',
      'API Access',
      'Custom Branding',
      'Integration Support',
      'Dedicated Account Manager',
      'Hotel/Apartment Specific Features',
      'Multi-Unit Property Management',
      'Enhanced Financial Management',
      'Advanced Tracking & GPS',
      'Custom Workflow Automation',
      'Lifetime Updates & Support',
      'On-Premise Deployment Option',
      'Custom Development Support'
    ],
    limits: {
      maxEmployees: -1, // Unlimited
      maxProperties: -1, // Unlimited
      maxAssets: -1, // Unlimited
      maxWorkOrdersPerMonth: -1, // Unlimited
      workOrderOverageRate: 0, // No overage charges
      storageGB: 1000,
      apiCallsPerDay: 200000,
      maxDevices: -1, // Unlimited
      maxSessions: -1
    },
    security: {
      maxConcurrentDevices: -1, // Unlimited
      maxConcurrentSessions: -1,
      sessionTimeoutMinutes: 240,
      deviceVerificationRequired: true,
      locationTrackingEnabled: true,
      forceLogoutOnNewDevice: false,
      suspiciousActivityDetection: true
    }
  }
};

// Security event types
export enum SecurityEventType {
  DEVICE_LOGIN = 'device_login',
  DEVICE_LOGOUT = 'device_logout',
  DEVICE_BLOCKED = 'device_blocked',
  SESSION_EXPIRED = 'session_expired',
  SUSPICIOUS_ACTIVITY = 'suspicious_activity',
  LOCATION_CHANGE = 'location_change',
  DEVICE_VERIFICATION_FAILED = 'device_verification_failed'
}

export interface SecurityEvent {
  id: string;
  user_id: string;
  tenant_id: string;
  event_type: SecurityEventType;
  device_fingerprint?: string;
  ip_address: string;
  user_agent: string;
  location_data?: LocationData;
  event_data: any;
  created_at: string;
}

// Billing interfaces
export interface SubscriptionBilling {
  id: string;
  subscription_id: string;
  tenant_id: string;
  amount: number;
  currency: string;
  billing_cycle: BillingCycle;
  status: 'pending' | 'paid' | 'failed' | 'refunded';
  due_date: string;
  paid_date?: string;
  payment_method?: string;
  created_at: string;
}

// Usage tracking
export interface SubscriptionUsage {
  id: string;
  tenant_id: string;
  date: string;
  employees_count: number;
  properties_count: number;
  assets_count: number;
  work_orders_count: number;
  storage_used_gb: number;
  api_calls_count: number;
  active_devices_count: number;
  active_sessions_count: number;
  created_at: string;
}

// Analytics
export interface SubscriptionAnalytics {
  totalSubscriptions: number;
  activeSubscriptions: number;
  revenueByTier: Record<string, number>;
  averageRevenuePerUser: number;
  churnRate: number;
  securityEvents: SecurityEvent[];
  deviceUsage: {
    totalDevices: number;
    activeDevices: number;
    blockedDevices: number;
  };
}

// Admin settings
export interface AdminSubscriptionSettings {
  id: string;
  tenant_id: string;
  can_set_free: boolean;
  can_override_limits: boolean;
  can_manage_billing: boolean;
  can_view_analytics: boolean;
  can_manage_security: boolean;
  created_at: string;
  updated_at: string;
}

// Upgrade paths
export interface SubscriptionUpgradePath {
  fromTier: SubscriptionTier;
  toTier: SubscriptionTier;
  reason: string;
  benefits: string[];
  estimatedSavings?: number;
}

export const UPSELL_STRATEGIES: SubscriptionUpgradePath[] = [
  {
    fromTier: SubscriptionTier.SOLO,
    toTier: SubscriptionTier.BASIC_PLUS,
    reason: 'Need more work orders and team collaboration',
    benefits: [
      '55 more work orders per month (75 vs 20)',
      'Support for up to 10 employees',
      'Multi-device access (2 devices)',
      'Enhanced reporting and analytics',
      'Priority support'
    ],
    estimatedSavings: 15
  },
  {
    fromTier: SubscriptionTier.BASIC_PLUS,
    toTier: SubscriptionTier.PROFESSIONAL,
    reason: 'Growing team needs more capacity and features',
    benefits: [
      '75 more work orders per month (150 vs 75)',
      'Support for up to 50 employees',
      'Multi-device access (5 devices)',
      'Scheduled maintenance visits',
      'Specialized training resources',
      'API access for integrations'
    ],
    estimatedSavings: 25
  },
  {
    fromTier: SubscriptionTier.PROFESSIONAL,
    toTier: SubscriptionTier.PREMIUM,
    reason: 'Enterprise-level needs with unlimited capacity',
    benefits: [
      'Unlimited work orders and employees',
      'Unlimited device access',
      '24/7 emergency support',
      'Customized maintenance plans',
      'Advanced training sessions',
      'Dedicated account manager'
    ],
    estimatedSavings: 40
  },
  {
    fromTier: SubscriptionTier.PREMIUM,
    toTier: SubscriptionTier.SAAS_ENGINEER,
    reason: 'Hotel/multi-unit property specific needs',
    benefits: [
      'Hotel-specific features and tools',
      'Parts inventory management',
      'Preventive maintenance system',
      'Enhanced financial management',
      'Multi-unit property management',
      'Custom workflow automation'
    ],
    estimatedSavings: 0 // Negotiated pricing
  },
  {
    fromTier: SubscriptionTier.SAAS_ENGINEER,
    toTier: SubscriptionTier.PERPETUAL_LICENSE,
    reason: 'Lifetime access with one-time payment',
    benefits: [
      'Lifetime unlimited access',
      'On-premise deployment option',
      'Custom development support',
      'Lifetime updates and support',
      'No recurring monthly fees'
    ],
    estimatedSavings: 100 // Long-term savings
  }
];

// Marketing materials
export const SUBSCRIPTION_MARKETING: Record<SubscriptionTier, {
  title: string;
  subtitle: string;
  description: string;
  highlights: string[];
  cta: string;
}> = {
  [SubscriptionTier.SOLO]: {
    title: 'Solo Plan',
    subtitle: 'Perfect for individual contractors',
    description: 'Essential tools for solo maintenance professionals. Single device access with 20 work orders per month.',
    highlights: [
      '20 work orders per month',
      '50 assets tracking',
      '2GB photo storage',
      'Single device security',
      '$1.99 per additional work order'
    ],
    cta: 'Start with Solo'
  },
  [SubscriptionTier.BASIC_PLUS]: {
    title: 'Basic Plan Plus',
    subtitle: 'Ideal for small maintenance teams',
    description: 'Enhanced features for growing teams. Multi-device access with 75 work orders per month.',
    highlights: [
      '75 work orders per month',
      '200 assets tracking',
      '10GB photo storage',
      'Multi-device security (2 devices)',
      'Priority support',
      '$1.99 per additional work order'
    ],
    cta: 'Choose Basic Plus'
  },
  [SubscriptionTier.PROFESSIONAL]: {
    title: 'Professional Plan',
    subtitle: 'For established maintenance companies',
    description: 'Comprehensive solution for professional maintenance operations. Advanced features with 150 work orders per month.',
    highlights: [
      '150 work orders per month',
      '500 assets tracking',
      '25GB photo storage',
      'Multi-device security (5 devices)',
      'Scheduled maintenance visits',
      'API access',
      '$1.99 per additional work order'
    ],
    cta: 'Go Professional'
  },
  [SubscriptionTier.PREMIUM]: {
    title: 'Premium Plan',
    subtitle: 'Enterprise-level maintenance management',
    description: 'Unlimited capacity with premium features. Perfect for large maintenance operations and enterprise clients.',
    highlights: [
      'Unlimited work orders & employees',
      'Unlimited asset tracking',
      '100GB photo storage',
      'Unlimited device access',
      '24/7 emergency support',
      'Dedicated account manager',
      'Custom integrations'
    ],
    cta: 'Upgrade to Premium'
  },
  [SubscriptionTier.SAAS_ENGINEER]: {
    title: 'SaaS Engineer Plan',
    subtitle: 'For large hotels & multi-unit properties',
    description: 'Negotiated pricing for stationary engineers in hotels over 301 rooms. All premium features plus hotel-specific tools.',
    highlights: [
      'Negotiated monthly pricing ($1,500-$5,000)',
      'Unlimited work orders & employees',
      '500GB photo storage',
      'Parts inventory management',
      'Preventive maintenance system',
      'Hotel-specific features',
      'Dedicated account manager'
    ],
    cta: 'Contact Sales'
  },
  [SubscriptionTier.PERPETUAL_LICENSE]: {
    title: 'Perpetual License',
    subtitle: 'One-time payment for lifetime access',
    description: 'One-time upfront fee for unlimited lifetime access. Perfect for large hotels over 1000 rooms.',
    highlights: [
      'One-time payment ($15,000-$100,000)',
      'Lifetime unlimited access',
      '1TB photo storage',
      'All premium features included',
      'On-premise deployment option',
      'Custom development support',
      'Lifetime updates & support'
    ],
    cta: 'Contact Sales'
  }
};
